// Small building blocks shared across pages.
import type { ReactNode } from 'react';
import { useId } from 'react';
import { CalendarDays, Check, Copy, Globe, Link2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { COLORS, TIMEZONES, VISIBILITY } from '@/lib/format';
import type { Visibility } from '@/lib/types';
import { notify } from '@/lib/notify';

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-balance">{title}</h1>
        {description ? <p className="text-muted-foreground mt-1 max-w-2xl text-sm">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ title, body, children, icon }: { title: string; body?: ReactNode; children?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="bg-card flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center">
      <div className="bg-brand-soft text-primary grid size-11 place-items-center rounded-xl">{icon ?? <CalendarDays className="size-5" />}</div>
      <h3 className="font-semibold">{title}</h3>
      {body ? <p className="text-muted-foreground max-w-md text-sm">{body}</p> : null}
      {children ? <div className="mt-1 flex flex-wrap justify-center gap-2">{children}</div> : null}
    </div>
  );
}

export function Field({ label, hint, children, htmlFor, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; htmlFor?: string; className?: string }) {
  return (
    <div className={cn('grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  );
}

export function copyText(text: string, what = 'Link') {
  if (!navigator.clipboard) return notify('Couldn’t copy — select the text and copy it manually', { kind: 'error' });
  navigator.clipboard.writeText(text).then(
    () => notify(`${what} copied`),
    () => notify('Couldn’t copy — select the text and copy it manually', { kind: 'error' }),
  );
}

export function CopyField({ value, label, mono = false, primary = false }: { value: string; label: string; mono?: boolean; primary?: boolean }) {
  return (
    <div className="flex gap-2">
      <Input readOnly value={value} aria-label={label} onFocus={(e) => e.target.select()} className={cn('min-w-0 flex-1', mono && 'font-mono text-xs')} />
      <Button type="button" variant={primary ? 'default' : 'outline'} onClick={() => copyText(value, label)}>
        <Copy /> Copy
      </Button>
    </div>
  );
}

export function FeedDot({ color, className }: { color: string; className?: string }) {
  return <span className={cn('inline-block size-2.5 shrink-0 rounded-[3px]', className)} style={{ background: color }} />;
}

const VIS_ICON = { private: Lock, unlisted: Link2, public: Globe };
export function VisibilityBadge({ v }: { v: Visibility }) {
  const Icon = VIS_ICON[v];
  return (
    <Badge variant="secondary" className="gap-1 font-normal">
      <Icon className="size-3" /> {VISIBILITY[v].label}
    </Badge>
  );
}

export function VisibilityPicker({ value, onChange, name = 'visibility' }: { value: Visibility; onChange: (v: Visibility) => void; name?: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Who can subscribe">
      {(Object.keys(VISIBILITY) as Visibility[]).map((k) => {
        const Icon = VIS_ICON[k];
        const on = value === k;
        return (
          <label key={k} className={cn('hover:bg-accent/50 flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-sm transition-colors has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50', on && 'border-primary bg-brand-soft hover:bg-brand-soft')}>
            <input type="radio" className="sr-only" name={name} value={k} checked={on} onChange={() => onChange(k)} />
            <span className="flex items-center gap-1.5 font-medium">
              <Icon className="size-3.5" /> {VISIBILITY[k].label}
              {on ? <Check className="text-primary ml-auto size-4" /> : null}
            </span>
            <span className="text-muted-foreground text-xs leading-snug">{VISIBILITY[k].desc}</span>
          </label>
        );
      })}
    </div>
  );
}

export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Color">
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={c === value}
          aria-label={c}
          onClick={() => onChange(c)}
          className={cn('focus-visible:ring-ring/50 size-7 rounded-full ring-offset-2 ring-offset-background transition-transform outline-none hover:scale-110 focus-visible:ring-[3px]', c === value && 'ring-foreground/70 ring-2')}
          style={{ background: c }}
        />
      ))}
    </div>
  );
}

export function TimezoneSelect({ value, onChange, id, disabled }: { value: string; onChange: (v: string) => void; id?: string; disabled?: boolean }) {
  const fallback = useId();
  return (
    <select
      id={id ?? fallback}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full min-w-0 rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:ring-[3px] disabled:opacity-50"
    >
      {(TIMEZONES.includes(value) ? TIMEZONES : [value, ...TIMEZONES]).map((z) => (
        <option key={z} value={z}>
          {z.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

export function FormError({ children }: { children?: ReactNode }) {
  return children ? (
    <p role="alert" className="text-destructive text-sm">
      {children}
    </p>
  ) : null;
}
